import {
  Component,
  OnInit,
  ViewChild,
  Input,
  OnChanges,
  SimpleChanges,
  OnDestroy
} from '@angular/core';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import {
  TabulatorService,
  TransformationService,
  EntityCacheProvider,
  NotificationService,
  DialogService,
  UnmanagedDevicesService,
  TimeoutWrapper,
  UserAuth
} from '@nrwl/common-lib';
import { ViaviTableComponent } from '@nrwl/viavi-tabulator';
import { AddUnmanagedDeviceComponent } from '../add-unmanaged-device/add-unmanaged-device.component';
import { Papa } from 'ngx-papaparse';

export const SiteRelatedCSVFields = [
  'Action',
  'Name',
  'Vendor',
  'Model',
  'Version',
  'Site',
  'TWAMP Control Server Ip',
  'TWAMP Control Server Port',
  'TWAMP Light Reflector Ip',
  'TWAMP Light Reflector Port',
  'TWAMP Ping Reflector Ip',
  'L2 Loopback',
  'L3 IPv4 Loopback',
  'L3 IPv6 Loopback'
];

export const LocationRelatedCSVFields = [
  'Action',
  'Name',
  'Vendor',
  'Model',
  'Version',
  'Location',
  'Latitude',
  'Longitude',
  'TWAMP Control Server Ip',
  'TWAMP Control Server Port',
  'TWAMP Light Reflector Ip',
  'TWAMP Light Reflector Port',
  'TWAMP Ping Reflector Ip',
  'L2 Loopback',
  'L3 IPv4 Loopback',
  'L3 IPv6 Loopback'
];

@Component({
  selector: 'nrwl-unmanaged-device-list',
  templateUrl: './unmanaged-device-list.component.html',
  styleUrls: ['./unmanaged-device-list.component.scss'],
  providers: [TimeoutWrapper]
})
export class UnmanagedDeviceListComponent implements OnInit, OnChanges, OnDestroy {
  @Input() loadNewData: boolean;
  @Input() exportUnmanagedDevice: Subject<boolean>;
  @Input() refreshTable?: BehaviorSubject<boolean>;
  @ViewChild(ViaviTableComponent) child: ViaviTableComponent;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  table: any;
  timeout: TimeoutWrapper;
  siteEntity = {};
  resetCheckbox: Subject<string[]>;
  siteList = [];
  allUnmanagedDevices = [];
  showWaitDialog = false;
  waitMsg = '';
  waitProgress = 0;
  hasReadWriteRole = false;
  isLoading = false;
  updateDeviceDialogRef: MatDialogRef<AddUnmanagedDeviceComponent>;
  private cacheObj: Subscription;
  private readonly unsubscribe: Subject<void> = new Subject();
  constructor(
    private readonly userAuth: UserAuth,
    private readonly transformService: TransformationService,
    private readonly tabulatorService: TabulatorService,
    private unmanagedDevicesService: UnmanagedDevicesService,
    readonly entityCache: EntityCacheProvider,
    private notificationService: NotificationService,
    private dialogService: DialogService,
    private dialog: MatDialog,
    private papa: Papa,
    timeout: TimeoutWrapper
  ) {
    this.timeout = timeout;
    this.table = this.tabulatorService.commonTableConfiguration();
    this.table.rowHeight = 56;
    this.table.height = window.innerHeight - 366 + 'px';
    this.table.persistenceID = 'unmanaged-device-management';
    this.resetCheckbox = new Subject<string[]>();
  }

  ngOnInit(): void {
    this.updateReadWriteRoleInfo();
    this.fetchSiteData();
    this.cacheObj = this.exportUnmanagedDevice.subscribe(res => {
      if (res) {
        this.downloadUnManagedDevice();
      }
    });
    this.cacheObj = this.refreshTable.subscribe(refresh => {
      if (refresh) {
        this.refreshUnmanagedDevicesList();
      }
    });
  }

  refreshUnmanagedDevicesList(): void {
    this.isLoading = true;
    this.unmanagedDevicesService
      .updateUnmanagedDevices(this.allUnmanagedDevices)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(
        response => {
          this.isLoading = false;
        },
        () => {
          this.isLoading = false;
        }
      );
  }

  updateReadWriteRoleInfo(): void {
    this.hasReadWriteRole = this.userAuth.isAuthedFor('READ_WRITE');
  }

  fetchSiteData(): void {
    this.siteEntity = this.entityCache.findEntityCache('site');
    if (this.siteEntity) {
      this.entityCache
        .findEntityCache('site')
        .getValuesAsync()
        .pipe(takeUntil(this.unsubscribe))
        .subscribe(site => {
          this.siteList = site;
          this.loadAllDevices();
        });
    } else {
      this.timeout.add(() => {
        this.loadAllDevices();
      }, 0);
    }
  }
  loadAllDevices(): void {
    this.cacheObj = this.unmanagedDevicesService.cache.subscribe(res => {
      this.allUnmanagedDevices = res;
      const data = this.formatData(res);
      const unmanagedData = this.transformService.transformData('unmanaged-device', data);
      this.table.columns = unmanagedData.column;
      this.table.data = unmanagedData.data;
      this.table.initialSort = [{ column: 'name', dir: 'asc' }];
      this.table = Object.assign({}, this.table);
    });
  }
  //eslint-disable-next-line
  formatData(data: any): any {
    const formattedData = [];
    data.forEach(item => {
      const device = {
        id: item.deviceData.id,
        name: item.deviceData.name,
        location: this.siteEntity
          ? this.createSiteLocationObj(item.deviceData.site)
          : this.createLocationObj(item.deviceData?.location || ''),
        model: { label: item.deviceData.model?.model, sublabel: item.deviceData?.model?.vendor },
        type: {
          col1: {},
          col2: {}
        },
        ip: {
          col1: {},
          col2: {}
        },
        vendor: item.deviceData.model?.vendor
      };

      for (const key in item.deviceData.capabilities) {
        switch (key) {
          case 'twamp-reflector':
            device.type.col1['label'] = 'TWAMP Full';
            device.ip.col1['label'] = item.deviceData.capabilities[key]['control-server-ip'];
            break;
          case 'twamp-light-reflector':
            device.type.col1['sublabel'] = 'TWAMP Light';
            device.ip.col1['sublabel'] = item.deviceData.capabilities[key].ip;
            break;
          case 'twamp-ping-reflector':
            device.type.col1['extra'] = 'TWAMP Ping';
            device.ip.col1['extra'] = item.deviceData.capabilities[key].ip;
            break;
          case 'l2-loopback':
            device.type.col2['label'] = 'L2 Loopback';
            device.ip.col2['label'] = item.deviceData.capabilities[key].mac;
            break;
          case 'l3-ipv4-loopback':
            device.type.col2['sublabel'] = 'L3 IPV4 Loopback';
            device.ip.col2['sublabel'] = item.deviceData.capabilities[key].ip;
            break;
          case 'l3-ipv6-loopback':
            device.type.col2['extra'] = 'L3 IPV6 Loopback';
            device.ip.col2['extra'] = item.deviceData.capabilities[key].ip;
            break;
        }
      }

      // Aweful but it put the TWAMP light as label if TWAMP full device is missing
      // Also does the same for ping if full and light are missing.
      if (!device.type.col1['label'] && device.type.col1['sublabel']) {
        device.type.col1['label'] = device.type.col1['sublabel'];
        device.ip.col1['label'] = device.ip.col1['sublabel'];
        device.type.col1['sublabel'] = '';
        device.ip.col1['sublabel'] = '';
      }
      if (
        !device.type.col1['label'] &&
        !device.type.col1['sublabel'] &&
        device.type.col1['extra']
      ) {
        device.type.col1['label'] = device.type.col1['extra'];
        device.ip.col1['label'] = device.ip.col1['extra'];
        device.type.col1['extra'] = '';
        device.ip.col1['extra'] = '';
      }

      if (!device.type.col2['label'] && device.type.col2['sublabel']) {
        device.type.col2['label'] = device.type.col2['sublabel'];
        device.ip.col2['label'] = device.ip.col2['sublabel'];
        device.type.col2['sublabel'] = '';
        device.ip.col2['sublabel'] = '';
      }
      if (
        !device.type.col2['label'] &&
        !device.type.col2['sublabel'] &&
        device.type.col2['extra']
      ) {
        device.type.col2['label'] = device.type.col2['extra'];
        device.ip.col2['label'] = device.ip.col2['extra'];
        device.type.col2['extra'] = '';
        device.ip.col2['extra'] = '';
      }

      formattedData.push(device);
    });
    return formattedData;
  }
  createSiteLocationObj(id: string): { label: string; sublabel: string } {
    const location = this.siteList.find(item => Number(item.sitesData.id) === Number(id));
    let siteLocationObj = {
      label: id,
      sublabel: ''
    };
    if (location) {
      siteLocationObj = {
        label: location.sitesData.name,
        sublabel: location.sitesData.locationname
      };
    }
    return siteLocationObj;
  }
  createLocationObj(name: string): { label: string; sublabel: string } {
    return {
      label: name,
      sublabel: ''
    };
  }
  //eslint-disable-next-line
  cancelWaitDialog() {
    this.showWaitDialog = false;
  }

  //eslint-disable-next-line
  async removeDevices(service: any, params: any) {
    if (params.items && params.items.length > 0) {
      const itemsPerIteration = 200;
      params.self.showWaitDialog = true;
      params.self.waitMsg = 'Removing devices...';
      params.self.waitProgress = 0;
      try {
        for (let i = 0; i < params.items.length; i += itemsPerIteration) {
          if (!params.self.showWaitDialog) break;
          const items = params.items.slice(i, i + itemsPerIteration);
          params.self.waitProgress = ((i + itemsPerIteration) / params.items.length) * 100;
          const payload = items.map(item => {
            return { id: item, type: 'unmanaged' };
          });
          await service.unmanagedDevicesService.deleteDevices(payload).toPromise();
        }
        service.resetCheckbox.next(params.items);
        service.updateCache(service.allUnmanagedDevices);
        if (params.self.showWaitDialog) {
          params.self.showWaitDialog = false;
          service.notificationService.onSuccess(`${params.items.length} device(s) removed`);
        } else {
          service.notificationService.onSuccess(
            `Remove devices cancelled. Some devices may not have been removed.`
          );
        }
      } catch (err) {
        params.self.showWaitDialog = false;
        service.notificationService.onFail(`Could not remove all devices: ${err.error.message}`);
      }
    } else {
      service.notificationService.onSuccess(`No devices selected for removal`);
    }
  }

  onBulkRemove(): void {
    this.showDeleteDialog(this.getSelectedItemsIds());
  }
  //eslint-disable-next-line
  showDeleteDialog(data): void {
    let device = null;
    if (data.length === 1) {
      device = this.allUnmanagedDevices.find(item => item.deviceData.id === data[0]);
    }
    const param = {
      id: 'unmanaged.device.delete',
      callBackService: this,
      alternateTitle:
        data.length > 1
          ? 'Remove ' + data.length + ' unmanaged devices?'
          : 'Remove ' + device.deviceData.name,
      alternateMsg:
        data.length > 1
          ? 'This action will permanently remove these unmanaged devices. Would you like to proceed?'
          : '',
      callBackFuntion: this.removeDevices,
      callBackFnParams: {
        items: data,
        self: this
      }
    };
    this.dialogService.showDialog('confirm', 'body', param);
  }
  // eslint-disable-next-line
  onRowActionClick(actionItem: any): void {
    if (actionItem.label === 'remove') {
      this.showDeleteDialog([actionItem.rowData.id]);
    } else if (actionItem.label === 'edit') {
      const data = this.allUnmanagedDevices.find(
        item => item.deviceData.id === actionItem.rowData.id
      );
      this.updateDeviceDialogRef = this.dialog.open(AddUnmanagedDeviceComponent, {
        width: '70rem',
        height: '71rem',
        disableClose: true,
        data: { device: data, editMode: true }
      });
      this.updateDeviceDialogRef.afterClosed().subscribe(() => {
        this.updateCache(this.allUnmanagedDevices);
      });
    }
  }
  //eslint-disable-next-line
  updateCache(data: any) {
    this.unmanagedDevicesService.updateUnmanagedDevices(data);
  }

  downloadUnManagedDevice(): void {
    const fields = this.siteEntity ? SiteRelatedCSVFields : LocationRelatedCSVFields;
    let devices = [];

    // priority of items to download
    // 1. items have been selected then downloaded
    // 2. items have been filtered then downloaded
    // 3. download all items if not selected or filtered
    const filterCount = this.child.filteredCount();
    if (this.child.selectedCount > 0) {
      const ids = this.getSelectedItemsIds();
      devices = this.allUnmanagedDevices.filter(item => {
        return ids.includes(item.deviceData.id);
      });
    } else if (filterCount > 0 && filterCount < this.allUnmanagedDevices.length) {
      const ids = this.getFilteredItemsIds();
      devices = this.allUnmanagedDevices.filter(item => {
        return ids.includes(item.deviceData.id);
      });
    } else {
      devices = this.allUnmanagedDevices;
    }
    const data = this.getFormattedData(devices);

    const sampleCSVFile = this.papa.unparse({
      data,
      fields
    });
    const blob = new Blob([sampleCSVFile]);
    const a = document.createElement('a');
    //eslint-disable-next-line
    a.href = URL.createObjectURL(blob);
    a.download = 'fusion-unmanaged-devices.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }
  //eslint-disable-next-line
  getFormattedData(devices: any): any {
    const data = [];
    if (devices.length) {
      devices.forEach(item => {
        const object = {};
        object['Name'] = item.deviceData.name;
        object['Vendor'] = item.deviceData.model.vendor;
        object['Model'] = item.deviceData.model.model || '';
        object['Version'] = item.deviceData.model.version || '';

        if (this.siteEntity) {
          object['Site'] =
            item.deviceData.site > 0 ? this.createSiteLocationObj(item.deviceData.site).label : '';
        } else {
          object['Location'] = item.deviceData?.location || '';
          object['Latitude'] =
            item.deviceData.lat && item.deviceData.lat !== 0 ? item.deviceData.lat : '';
          object['Longitude'] =
            item.deviceData.lon && item.deviceData.lon !== 0 ? item.deviceData.lon : '';
        }

        if (item.deviceData.capabilities['twamp-reflector']) {
          object['TWAMP Control Server Ip'] =
            item.deviceData.capabilities['twamp-reflector']['control-server-ip'];
          object['TWAMP Control Server Port'] =
            item.deviceData.capabilities['twamp-reflector']['control-server-port'];
        }

        if (item.deviceData.capabilities['twamp-light-reflector']) {
          object['TWAMP Light Reflector Ip'] =
            item.deviceData.capabilities['twamp-light-reflector'].ip;
          object['TWAMP Light Reflector Port'] =
            item.deviceData.capabilities['twamp-light-reflector']['udp-port'];
        }

        if (item.deviceData.capabilities['twamp-ping-reflector']) {
          object['TWAMP Ping Reflector Ip'] =
            item.deviceData.capabilities['twamp-ping-reflector'].ip;
        }

        if (item.deviceData.capabilities['l2-loopback']) {
          object['L2 Loopback'] = item.deviceData.capabilities['l2-loopback'].mac;
        }

        if (item.deviceData.capabilities['l3-ipv4-loopback']) {
          object['L3 IPv4 Loopback'] = item.deviceData.capabilities['l3-ipv4-loopback'].ip;
        }

        if (item.deviceData.capabilities['l3-ipv6-loopback']) {
          object['L3 IPv6 Loopback'] = item.deviceData.capabilities['l3-ipv6-loopback'].ip;
        }

        data.push(object);
      });
    }
    return data;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.loadNewData.currentValue) {
      this.updateCache(this.allUnmanagedDevices);
    }
  }
  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
    if (this.cacheObj) {
      this.cacheObj.unsubscribe();
    }
  }
  devicesLoadingComplete(): boolean {
    return this.unmanagedDevicesService.deviceQueryComplete;
  }
  get isActionsDisabled(): boolean {
    if (this.getSelectedItemsIds().length === 0) return true;
  }
  private getSelectedItemsIds(): string[] {
    return this.child?.selection?.map(x => x.id) || [];
  }
  private getFilteredItemsIds(): string[] {
    return this.child?.filteredItems()?.map(x => x.id) || [];
  }
}
